import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../../auth/contexts/AuthContext';

interface Project {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  progress: number;
  path: string;
  color: {
    from: string;
    to: string;
  };
}

interface ProjectsOverviewProps {
  onProjectSelect: (projectPath: string) => void;
}

const ProjectsOverview: React.FC<ProjectsOverviewProps> = ({ onProjectSelect }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user } = useAuth();

  // Tüm mevcut projeler
  const allProjects: Project[] = [
    {
      id: 'translator',
      name: 'Çevirmen',
      description: 'Profesyonel çeviri hizmetleri ve belge işleme platformu',
      path: 'translate',
      progress: 100,
      color: {
        from: '#02c2ff',
        to: '#0199cc'
      },
      icon: (
        <svg viewBox="0 0 24 24" fill="none" className="w-8 h-8">
          <path d="M5 8l6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M4 14l6-6 2-3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M2 5h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M7 2h1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M22 22l-5-10-5 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M14 18h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )
    },
    {
      id: 'fr-update',
      name: 'İçerik Güncelleme',
      description: 'Finansal raporlama belgeleri güncelleme ve yönetim platformu',
      path: 'fr-update',
      progress: 80,
      color: {
        from: '#10b981',
        to: '#059669'
      },
      icon: (
        <svg viewBox="0 0 24 24" fill="none" className="w-8 h-8">
          <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M15 3v4a2 2 0 002 2h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )
    },
    {
      id: 'coming-soon',
      name: 'Proje 3',
      description: 'Yeni özellikler ve araçlar yakında sizlerle',
      path: '',
      progress: 0,
      color: {
        from: '#8b5cf6',
        to: '#7c3aed'
      },
      icon: (
        <svg viewBox="0 0 24 24" fill="none" className="w-8 h-8">
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" stroke="currentColor" strokeWidth="2"/>
        </svg>
      )
    }
  ];

  // Tüm projeleri göster
  const projects = allProjects;

  return (
    <div className="relative overflow-hidden">
      
      {/* Hero Section */}
      <div className="relative text-center mb-16">
        <div className="inline-flex items-center px-6 py-3 mb-8 bg-gradient-to-r from-[#02c2ff]/10 to-[#0199cc]/10 rounded-full border border-[#02c2ff]/20 backdrop-blur-sm">
          <div className="w-2 h-2 bg-[#02c2ff] rounded-full mr-3 animate-pulse"></div>
          <span className="text-sm font-medium text-[#02c2ff] tracking-wide">Finar-AI ENTERPRISE PLATFORM</span>
        </div>
        
        <h1 className="text-5xl md:text-7xl font-bold text-gray-900 dark:text-white mb-8 leading-tight">
          <span className="bg-gradient-to-r from-gray-900 to-gray-700 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
            Hoş Geldiniz
          </span>
          <br/>
          <span className="bg-gradient-to-r from-[#02c2ff] to-[#0199cc] bg-clip-text text-transparent">
            {user?.name || 'Kullanıcı'}
          </span>
        </h1>
        
        <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-300 mb-12 max-w-4xl mx-auto leading-relaxed">
          Finar-AI platformuna hoş geldiniz. İş akışlarınızı yönetin ve projelerinizi takip edin.
        </p>
      </div>

      {/* Projects Grid */}
      <div className="mb-16">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Uygulamalar</h2>
          <span className="text-sm text-gray-500 dark:text-gray-400">{projects.length} Uygulama</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {projects.map((project) => (
          <div 
            key={project.id} 
            className={`group relative bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-500 p-8 border border-gray-200/50 dark:border-gray-700/50 hover:border-[${project.color.from}]/30 overflow-hidden ${
              project.path ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'
            }`}
            onClick={() => project.path && navigate(`/${project.path}`)}
          >
            {/* Background Pattern */}
            <div className="absolute top-0 right-0 w-32 h-32 rounded-full transform translate-x-16 -translate-y-16" 
                 style={{ background: `linear-gradient(to bottom left, ${project.color.from}10, transparent)` }}></div>
            
            <div className="relative z-10">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 shadow-lg text-white"
                   style={{ background: `linear-gradient(to bottom right, ${project.color.from}, ${project.color.to})` }}>
                {project.icon}
              </div>
              
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 group-hover:text-[#02c2ff] transition-colors">
                {project.name}
              </h3>
              
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-6">
                {project.description}
              </p>
              
              {/* Progress Section */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {project.progress > 0 ? 'Tamamlanma' : 'Yakında'}
                  </span>
                  <span className="text-sm font-bold" style={{ color: project.color.from }}>
                    {project.progress > 0 ? `${project.progress}%` : 'Geliyor'}
                  </span>
                </div>
                
                <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2 overflow-hidden">
                  <div 
                    className="h-full rounded-full transition-all duration-300 relative overflow-hidden"
                    style={{ 
                      width: `${project.progress}%`,
                      background: `linear-gradient(to right, ${project.color.from}, ${project.color.to})`
                    }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-pulse"></div>
                  </div>
                </div>
              </div>
              
              {/* Action Button */}
              <div className="flex items-center font-semibold text-sm" style={{ color: project.color.from }}>
                <span>{project.path ? 'Projeyi Aç' : 'Yakında Geliyor'}</span>
                {project.path && (
                  <svg className="ml-2 w-4 h-4 transform group-hover:translate-x-2 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3"></path>
                  </svg>
                )}
              </div>
            </div>
          </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProjectsOverview;
